import {
	getDefaultSoftphoneAudioSettings,
	SoftphoneEventBatcher,
	SoftphoneLeaderElection,
	SoftphoneQualityComputer,
	SoftphoneTabBus,
	type SoftphoneAudioCaptureModel,
	type SoftphoneAudioSettingsModel,
	type SoftphoneCallModel,
	type SoftphoneCallStateModel,
	type SoftphoneDeviceModel,
	type SoftphoneEventSenderModel,
	type SoftphoneLeaderRole,
	type SoftphoneObservabilityEventInputModel,
	type SoftphoneObservabilityEventTypeModel,
	type SoftphoneQualityWarningCodeModel,
	type SoftphoneQualityWarningModel,
	type SoftphoneSdpSummaryModel,
	type SoftphoneUplinkGovernorActionModel,
} from '@supply-mover-2-0/core';
import type { CallerIdMatchResultDto } from '@supply-mover-2-0/shared';
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
} from 'react';
import { useManagerCoachingSession } from './hooks/useManagerCoachingSession';
import { useRepCoachingState } from './hooks/useRepCoachingState';
import { SoftphoneSelfServiceQueryService } from './services/SoftphoneSelfServiceQueryService';
import {
	useSoftphoneStore,
	type SoftphoneCallView,
} from './store/softphoneStore';
import { shouldOpenWrapUp, wrapUpTalkSeconds } from './utils/wrapUpEligibility';

/**
 * localStorage key for the rep's audio tuning (PD-14902). Per-browser,
 * per-rep; the settings are a personal preference (their mic, their
 * headset), not tenant or call state, so they persist across sessions
 * and reloads independently of the auth store.
 */
const AUDIO_SETTINGS_STORAGE_KEY = 'softphone.audioSettings.v1';

/** Load persisted audio settings, falling back to defaults on any
 *  parse error or absent value. Merges over defaults so a settings
 *  shape that gains a field in a later release stays valid. */
function loadPersistedAudioSettings(): SoftphoneAudioSettingsModel {
	const defaults = getDefaultSoftphoneAudioSettings();
	try {
		const raw = window.localStorage.getItem(AUDIO_SETTINGS_STORAGE_KEY);
		if (raw == null || raw === '') {
			return defaults;
		}
		const parsed = JSON.parse(raw) as Partial<SoftphoneAudioSettingsModel>;
		return {
			...defaults,
			...parsed,
			capture: { ...defaults.capture, ...parsed.capture },
		};
	} catch {
		return defaults;
	}
}

/** Persist audio settings; swallow quota/serialization errors — losing
 *  persistence must never break call handling. */
function persistAudioSettings(settings: SoftphoneAudioSettingsModel): void {
	try {
		window.localStorage.setItem(
			AUDIO_SETTINGS_STORAGE_KEY,
			JSON.stringify(settings),
		);
	} catch {
		// Non-fatal — the in-memory store still reflects the change.
	}
}

/**
 * Emergency-number predicate matching the backend's
 * `SoftphoneE911Service.isEmergencyBypassNumber` rules. Kept in the
 * frontend so the pre-flight gate can short-circuit BEFORE making
 * the status-fetch round-trip (a 911 dial should never be slowed by
 * an E911 status check — the whole point of the bypass).
 *
 * Stays in sync with the backend list (911 / 933 / 988); if the
 * federal list ever expands we update both.
 */
function isE911BypassTarget(rawTo: string): boolean {
	if (rawTo == null) return false;
	const digits = rawTo.replace(/\D/g, '');
	const trimmed = digits.startsWith('1') ? digits.slice(1) : digits;
	return trimmed === '911' || trimmed === '933' || trimmed === '988';
}

/** Stat-sampling cadence during a connected call. Matches the
 *  10s persistence window in SoftphoneQualityComputer (2 samples). */
const DEFAULT_STAT_SAMPLING_INTERVAL_MS = 5_000;

/** Persist every Nth stat sample as a `quality.sample` observability
 *  event (PD-14816) — at the 5s cadence, every 3rd sample ≈ one
 *  persisted quality snapshot per 15s of call. Sampling still runs at
 *  the full cadence for the warning computer; only persistence is
 *  throttled. */
const QUALITY_SAMPLE_PERSIST_EVERY = 3;

export interface SoftphoneObservabilityConfigModel {
	/** Where to ship batched events. Typically a fetch() to the
	 *  /api/softphone/observability/events endpoint. */
	sender: SoftphoneEventSenderModel;
	/** Override the batcher's flush cadence. Defaults to 5s. */
	flushIntervalMs?: number;
	/** Override the active-call stat-sampling cadence. Defaults to 5s. */
	statSamplingIntervalMs?: number;
}

/**
 * Imperative actions exposed by the SoftphoneProvider via React
 * context. UI components subscribe to render state through the
 * Zustand store; these are the *commands* they issue back into
 * the device.
 *
 * Follower-tab actions (`dial`, `requestLeadership`) post messages
 * onto the cross-tab bus instead of acting locally — the leader
 * tab is the only one that owns the actual softphone connection.
 */
export interface SoftphoneDialOptionsModel {
	/** E.164 caller-ID to dial out as. The backend rejects the call
	 * if this number isn't assigned to the calling user. When
	 * omitted, the backend will fall back to the user's
	 * `isDefaultOutbound` row (DD.9.1). */
	fromCallerId?: string;
	/**
	 * The CRM record the rep clicked to place this call (PD-14414).
	 * Drives the active-call bar's "who am I talking to" label
	 * immediately (no wait for the phone-map matcher) and propagates
	 * to the backend as TwiML CrmContext params so the call row's
	 * attribution reflects the rep's intent, not a phone-map guess.
	 * The call still fans out to every phone-map-matched record's
	 * call log — this context only decides the DISPLAY + primary
	 * attribution.
	 */
	crmContext?: SoftphoneDialCrmContextModel;
}

export interface SoftphoneDialCrmContextModel {
	entityType: 'client' | 'vendor';
	entityId: number;
	/** Record name shown in the active-call bar (e.g. "Acme Corp"). */
	displayName: string;
	/** The specific contact clicked, when the number belongs to a
	 *  person on the record (renders as "Jane Doe — Acme Corp"). */
	contactName?: string;
}

/**
 * Synthesizes the `CallerIdMatchResultDto` shape the active-call bar
 * already renders from a click-to-dial context, so the "who am I
 * talking to" label + CLIENT/VENDOR pill show instantly at dial time
 * with zero display-component changes (PD-14414).
 */
function buildClickContextMatch(
	ctx: SoftphoneDialCrmContextModel,
	toE164: string,
): CallerIdMatchResultDto {
	const displayName =
		ctx.contactName != null && ctx.contactName !== ''
			? `${ctx.contactName} — ${ctx.displayName}`
			: ctx.displayName;
	return {
		best: {
			matchType: ctx.entityType,
			entityId: ctx.entityId,
			displayName,
			recencyScore: null,
			rank: 0,
		},
		alternatives: [],
		confidence: 'unique',
		matchedPhoneE164: toE164,
	};
}

export interface SoftphoneActionsModel {
	/** Place an outbound call. From a follower tab this posts a
	 * dial-intent on the bus and the leader places the call. */
	dial: (toE164: string, options?: SoftphoneDialOptionsModel) => Promise<void>;
	answer: () => Promise<void>;
	decline: () => Promise<void>;
	hangup: () => Promise<void>;
	mute: () => Promise<void>;
	unmute: () => Promise<void>;
	hold: () => Promise<void>;
	unhold: () => Promise<void>;
	sendDTMF: (digits: string) => Promise<void>;
	blindTransfer: (toE164OrUserIdentity: string) => Promise<void>;
	setInputDevice: (deviceId: string) => Promise<void>;
	setOutputDevice: (deviceId: string) => Promise<void>;

	// Audio tuning (PD-14902). Each writes the device (live on the
	// current call), mirrors into the store, and persists per-rep.
	/** Outgoing mic makeup gain (1 = unity). */
	setInputGain: (gain: number) => void;
	/** Incoming playback gain (1 = unity). */
	setOutputVolume: (volume: number) => void;
	/** Toggle compression + presence lift on the incoming audio. */
	setReceivedAudioEnhancement: (enabled: boolean) => void;
	/** Update mic-capture processing (noise suppression / auto-gain /
	 *  echo cancellation). Async — the browser re-acquires the mic. */
	setAudioCaptureSettings: (
		capture: Partial<SoftphoneAudioCaptureModel>,
	) => Promise<void>;
	/** From a follower tab: ask the current leader to step down so
	 * this tab can take over (the "Switch" button on the
	 * `<CallActiveInOtherTabBanner>`). */
	requestLeadership: () => void;
	/** Call-waiting: end the current call and answer the pending one. */
	acceptPendingCall: () => Promise<void>;
	/** Call-waiting: decline the pending call, keep the current one. */
	declinePendingCall: () => Promise<void>;
	/**
	 * Returns the active call's local mic `MediaStream`, or `null` when
	 * no call is connected (or the provider can't surface one). Used by
	 * `useMuteLevelMeter` to attach a Web Audio analyser without owning
	 * the SDK call ref. Read-only — consumers MUST NOT stop or disable
	 * the stream's tracks.
	 */
	getLocalAudioStream: () => MediaStream | null;
}

interface SoftphoneTabBusMessageModel {
	type:
		| 'request-leadership'
		| 'leadership-denied'
		| 'dial-intent'
		| 'dial-failed'
		| 'leader-state';
	/** dial-intent target (E.164) */
	toE164?: string;
	/** dial-intent caller-ID override (E.164) — relayed from the
	 *  originating tab so the leader places the call from the
	 *  correct number (DD.9.1). Optional; when missing the backend
	 *  falls back to the user's `isDefaultOutbound` row. */
	fromCallerId?: string;
	/** dial-intent click-to-dial context (PD-14414) — relayed so the
	 *  leader tab's active-call bar shows the clicked record too. */
	crmContext?: SoftphoneDialCrmContextModel;
	/** dial-failed: error message surfaced back to the originating tab */
	error?: string;
	/** leader-state: whether the leader currently has an active call.
	 *  Drives the follower tab's banner copy (idle vs in-call). */
	hasActiveCall?: boolean;
	/** leadership-denied: human-readable reason ("call in progress") */
	reason?: string;
}

const SoftphoneActionsContext = createContext<SoftphoneActionsModel | null>(
	null,
);

export interface SoftphoneProviderPropsModel {
	/**
	 * Factory that creates the underlying device. Use
	 * `() => new MockSoftphoneDevice()` for development / Storybook;
	 * real Twilio / RC / etc. plugins land in Phase 2+.
	 *
	 * Accepting a factory (not an instance) lets the Provider create
	 * the device exactly once on mount and dispose on unmount,
	 * without cross-render leaks.
	 */
	deviceFactory: () => SoftphoneDeviceModel;
	/**
	 * Unique key per logged-in user session — typically
	 * `{tenantAlias}:{userId}`. Scopes the leader-election Web Lock
	 * and the cross-tab bus so different users / tenants on the same
	 * browser don't collide.
	 */
	resourceKey: string;
	/**
	 * Observability sink. When supplied, the Provider wires a
	 * SoftphoneEventBatcher across device + call + leader-election
	 * events, plus (when the device's capability matrix says so) a
	 * SoftphoneQualityComputer that samples `getStats()` every
	 * `statSamplingIntervalMs` and surfaces threshold-crossings as
	 * normalized quality warnings on the same store slice the
	 * Twilio first-class warnings would land on. Omit to disable
	 * observability entirely.
	 */
	observability?: SoftphoneObservabilityConfigModel;
	children: React.ReactNode;
}

/**
 * Root provider for the in-CRM softphone. Owns:
 *   - The browser-side `SoftphoneDeviceModel` (mock or real per `deviceFactory`).
 *   - A `SoftphoneLeaderElection` instance scoped to `resourceKey`.
 *   - A `SoftphoneTabBus` instance scoped to the same `resourceKey`.
 *
 * It mirrors device + call lifecycle into the Zustand store so
 * components re-render reactively, and exposes imperative actions
 * (dial, answer, hangup, mute, hold, transfer, device selection,
 * request-leadership) through React context.
 *
 * Only the elected leader tab actually registers with the device. A
 * follower tab keeps the device idle, listens to the bus for state
 * mirrors (future), and forwards user-initiated dial intents to the
 * leader. Switching the active tab posts a `request-leadership`
 * message — the current leader voluntarily releases its lock and the
 * requesting tab acquires.
 */
export const SoftphoneProvider: React.FC<SoftphoneProviderPropsModel> = ({
	deviceFactory,
	resourceKey,
	observability,
	children,
}) => {
	const deviceRef = useRef<SoftphoneDeviceModel | null>(null);
	const callRef = useRef<SoftphoneCallModel | null>(null);
	const pendingCallRef = useRef<SoftphoneCallModel | null>(null);
	const electionRef = useRef<SoftphoneLeaderElection | null>(null);
	const busRef = useRef<SoftphoneTabBus<SoftphoneTabBusMessageModel> | null>(
		null,
	);
	// Refs so the per-call event handlers always see the live values
	// without depending on closure-recapture across renders.
	const recordEventRef = useRef<
		((event: SoftphoneObservabilityEventInputModel) => void) | null
	>(null);
	const roleRef = useRef<SoftphoneLeaderRole>('follower');

	// Capture the observability config behind a ref so a consumer
	// passing an inline `observability={{ sender }}` literal doesn't
	// re-trigger the heavyweight wiring effect (which disposes the
	// device, leader-election, batcher — and any active call). The
	// effect below depends only on `resourceKey` — observability is
	// pulled out of the ref at mount and treated as stable for the life
	// of that mount. Consumers wanting to swap observability mid-life
	// must change `resourceKey` to force a remount; that's the intended
	// escape hatch.
	const observabilityRef = useRef(observability);
	observabilityRef.current = observability;

	// PD-14895 — `deviceFactory` gets the same treatment, and for the
	// same reason. It was previously a dependency of the wiring effect,
	// so a consumer passing an inline `deviceFactory={createFactory()}`
	// handed the provider a fresh function identity on every render and
	// every re-render disposed the device — hanging up on the customer
	// mid-call. A factory is only ever invoked once per mount, so there
	// is no value in reacting to a new one; `resourceKey` remains the
	// single honest signal that the session actually changed.
	const deviceFactoryRef = useRef(deviceFactory);
	deviceFactoryRef.current = deviceFactory;

	// Tier 2 coaching: rep-side badge state. Mounted at provider
	// scope so the store stays in sync regardless of which downstream
	// component renders the badge. The hook self-gates on whether
	// there's an active call with a persisted id.
	useRepCoachingState();

	// Tier 2 coaching: manager-side toolbar state. Same provider-
	// scope rationale; lets `<CoachingToolbar>` rehydrate after page
	// reload without losing the in-flight session id. Skips fetching
	// when the user has no `phone:coaching:any` permission — the
	// 403 is swallowed silently because the toolbar simply renders
	// nothing in that case.
	useManagerCoachingSession();

	const wireCallEvents = useCallback((call: SoftphoneCallModel): void => {
		const callId = call.id;
		const recordEvent = recordEventRef.current;

		const onStateChange = (state: SoftphoneCallStateModel): void => {
			const patch: Partial<SoftphoneCallView> = { state };
			if (state === 'connected') patch.connectedAt = Date.now();
			useSoftphoneStore.getState()._patchCurrentCall(patch);
			recordEvent?.({
				eventType: 'call.state-change',
				callId,
				role: roleRef.current,
				payload: { state },
			});
		};
		const onAccepted = (): void => {
			recordEvent?.({
				eventType: 'call.accepted',
				callId,
				role: roleRef.current,
				payload: {},
			});
		};
		const onDisconnected = (reason: {
			code: string;
			message: string;
		}): void => {
			// Capture partner label BEFORE clearing currentCall so the
			// disconnection-reason toast can render "Call ended with
			// Acme Corp" not just "Call ended".
			const previousCall = useSoftphoneStore.getState().currentCall;
			const partnerLabel =
				previousCall?.callerIdMatch?.best?.displayName != null &&
				previousCall.callerIdMatch.best.displayName !== ''
					? previousCall.callerIdMatch.best.displayName
					: (previousCall?.fromE164 ?? previousCall?.toE164 ?? null);
			// Connected calls, and every outbound call the rep placed
			// (answered or not — PD-17007), get a wrap-up. See
			// `shouldOpenWrapUp`.
			const persistedCallId = previousCall?.persistedCallId ?? null;

			useSoftphoneStore.getState()._setCurrentCall(null);
			useSoftphoneStore.getState()._setLastDisconnect({
				at: Date.now(),
				code: reason.code,
				message: reason.message,
				// `failed`/`network-error`/`busy`/`timeout` are the
				// "something went wrong" shapes; everything else is
				// just an end-of-call signal that doesn't need an
				// error-tone toast.
				isError:
					reason.code === 'failed' ||
					reason.code === 'network-error' ||
					reason.code === 'busy' ||
					reason.code === 'timeout',
				partnerLabel,
			});

			if (
				previousCall != null &&
				persistedCallId != null &&
				shouldOpenWrapUp(previousCall)
			) {
				const startedAt = previousCall.connectedAt ?? previousCall.startedAt;
				const endedAt = Date.now();
				const callerIdMatch = previousCall.callerIdMatch;
				const best = callerIdMatch?.best ?? null;
				useSoftphoneStore.getState()._setWrapUp({
					dbCallId: persistedCallId,
					partnerLabel,
					// Talk time only — zero for an outbound call nobody
					// answered.
					durationSeconds: wrapUpTalkSeconds(previousCall, endedAt),
					startedAt,
					endedAt,
					matchType: best?.matchType ?? null,
					matchEntityId: best?.entityId ?? null,
					matchDisplayName: best?.displayName ?? null,
					// PD-14419 — carried into the wrap-up "Change match"
					// control: alternatives first, original best for the
					// attribution audit row, the number for the
					// mastering-aware "save this number" affordances.
					alternatives: callerIdMatch?.alternatives ?? [],
					matcherSuggestedType: best?.matchType ?? null,
					matcherSuggestedEntityId: best?.entityId ?? null,
					externalPartyE164:
						previousCall.direction === 'inbound'
							? previousCall.fromE164
							: previousCall.toE164,
				});
			}

			callRef.current = null;
			recordEvent?.({
				eventType: 'call.disconnected',
				callId,
				role: roleRef.current,
				payload: { reasonCode: reason.code, reasonMessage: reason.message },
			});
			// Broadcast follower-visible state so the other tabs'
			// banner copy flips back from "Call in progress" to
			// "Phone ready" the moment the call ends. Read the bus via
			// `busRef` because this callback closure is created in
			// `wireCallEvents` (a useCallback outside the main effect)
			// and doesn't capture the local `bus` from the effect body.
			busRef.current?.post({ type: 'leader-state', hasActiveCall: false });
		};
		const onQualityWarning = (warning: SoftphoneQualityWarningModel): void => {
			useSoftphoneStore.getState()._addQualityWarning(warning);
			recordEvent?.({
				eventType: 'quality.warning-raised',
				callId,
				role: roleRef.current,
				payload: { ...warning },
			});
		};
		const onQualityWarningCleared = (code: string): void => {
			useSoftphoneStore.getState()._clearQualityWarning(code);
			recordEvent?.({
				eventType: 'quality.warning-cleared',
				callId,
				role: roleRef.current,
				payload: { code },
			});
		};
		const onDtmfReceived = (digits: string): void => {
			recordEvent?.({
				eventType: 'call.dtmf-received',
				callId,
				role: roleRef.current,
				payload: { digits },
			});
		};
		// PD-14930 — uplink governor stepped the send-bitrate cap. Persisted
		// so quality effects are measurable against Voice Insights.
		const onUplinkGovernorAction = (
			action: SoftphoneUplinkGovernorActionModel,
		): void => {
			recordEvent?.({
				eventType: 'quality.governor',
				callId,
				role: roleRef.current,
				payload: { ...action },
			});
		};
		// PD-14930 — one-time negotiated-SDP summary (codec / Opus inband
		// FEC / ptime): verifies the loss-resilience assumptions per call.
		const onSdpNegotiated = (summary: SoftphoneSdpSummaryModel): void => {
			recordEvent?.({
				eventType: 'quality.sdp',
				callId,
				role: roleRef.current,
				payload: { ...summary },
			});
		};
		const onMetadataUpdated = (): void => {
			// Conference-architecture inbound calls: matcher/persistedCallId/
			// recordingActive arrive ~200ms after the SDK's `incoming` event
			// via the incoming-context fetch. Patch the store so the call
			// pop re-renders with the matcher info.
			//
			// Click-to-dial (PD-14414): when the rep explicitly clicked a
			// record to place the call, that label is authoritative — the
			// async phone-map guess must not clobber it.
			const keepClickMatch =
				useSoftphoneStore.getState().currentCall?.callerIdMatchAuthoritative ===
				true;
			useSoftphoneStore.getState()._patchCurrentCall({
				...(keepClickMatch ? {} : { callerIdMatch: call.callerIdMatch }),
				persistedCallId: call.persistedCallId,
				recordingActive: call.recordingActive,
				transferContext: call.transferContext ?? null,
				fromE164: call.fromE164,
			});
		};

		call.on('stateChange', onStateChange);
		call.on('accepted', onAccepted);
		call.on('disconnected', onDisconnected);
		call.on('qualityWarning', onQualityWarning);
		call.on('qualityWarningCleared', onQualityWarningCleared);
		call.on('dtmfReceived', onDtmfReceived);
		call.on('metadataUpdated', onMetadataUpdated);
		call.on('uplinkGovernorAction', onUplinkGovernorAction);
		call.on('sdpNegotiated', onSdpNegotiated);
	}, []);

	useEffect(() => {
		const election = new SoftphoneLeaderElection(resourceKey);
		electionRef.current = election;

		const bus = new SoftphoneTabBus<SoftphoneTabBusMessageModel>(resourceKey);
		busRef.current = bus;

		const device = deviceFactoryRef.current();
		deviceRef.current = device;

		// ---- Audio tuning hydration (PD-14902) ----
		// Push the rep's persisted tuning into the fresh device before it
		// registers, so the very first call already reflects their saved
		// gains and mic-capture settings. The device applies these to its
		// processors on register; here we also mirror them into the store
		// so the controls render the right positions immediately.
		const persistedAudio = loadPersistedAudioSettings();
		device.setInputGain(persistedAudio.inputGain);
		device.setOutputVolume(persistedAudio.outputVolume);
		device.setReceivedAudioEnhancement(persistedAudio.enhanceReceivedAudio);
		void device.setAudioCaptureSettings(persistedAudio.capture);
		useSoftphoneStore.getState()._setAudioSettings(device.getAudioSettings());

		// ---- Observability wiring (optional) ----
		// When the consumer supplies an `observability` config we
		// instantiate the batcher, expose `recordEvent` to the rest of
		// the effect via a ref, and (for `qualityWarnings === 'computed'`
		// providers) instantiate the QualityComputer + start a stat-
		// sampling loop. None of this fires when observability is
		// disabled — the Provider works the same as before.
		const sessionId =
			typeof globalThis.crypto !== 'undefined' &&
			'randomUUID' in globalThis.crypto
				? globalThis.crypto.randomUUID()
				: `sess-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

		let batcher: SoftphoneEventBatcher | null = null;
		let qualityComputer: SoftphoneQualityComputer | null = null;
		let statSamplingHandle: ReturnType<typeof setInterval> | null = null;

		// Read observability ONCE at mount time. The ref keeps the
		// consumer's pointer stable for runtime reads (none today),
		// but the batcher / sampling are driven from the snapshot we
		// captured here. See the ref-declaration comment above.
		const observabilitySnapshot = observabilityRef.current;
		if (observabilitySnapshot != null) {
			batcher = new SoftphoneEventBatcher({
				sessionId,
				sender: observabilitySnapshot.sender,
				flushIntervalMs: observabilitySnapshot.flushIntervalMs,
			});
			batcher.start();
			recordEventRef.current = (event) => batcher?.record(event);

			if (device.capabilities.qualityWarnings === 'computed') {
				qualityComputer = new SoftphoneQualityComputer({
					onWarning: (warning) => {
						useSoftphoneStore.getState()._addQualityWarning(warning);
						batcher?.record({
							eventType: 'quality.warning-raised',
							callId: callRef.current?.id ?? null,
							role: roleRef.current,
							payload: { ...warning },
						});
					},
					onWarningCleared: (code: SoftphoneQualityWarningCodeModel) => {
						useSoftphoneStore.getState()._clearQualityWarning(code);
						batcher?.record({
							eventType: 'quality.warning-cleared',
							callId: callRef.current?.id ?? null,
							role: roleRef.current,
							payload: { code },
						});
					},
				});
			}

			const samplingMs =
				observabilitySnapshot.statSamplingIntervalMs ??
				DEFAULT_STAT_SAMPLING_INTERVAL_MS;
			let consecutiveSamplingFailures = 0;
			let samplesSincePersist = 0;
			statSamplingHandle = setInterval(() => {
				const call = callRef.current;
				if (call == null) return;
				if (call.state !== 'connected' && call.state !== 'on-hold') return;
				void (async () => {
					try {
						const stats = await call.getStats();
						qualityComputer?.sample(stats);
						// PD-14910 — surface the live sample to the store so the
						// connection-signal indicator reflects the CURRENT MOS /
						// jitter / RTT / loss, not just discrete threshold
						// warnings (which can sit just under the line while the
						// rep still hears degradation).
						useSoftphoneStore.getState()._setLiveCallStats(stats.audio);
						// PD-14816 — persist a throttled quality snapshot so
						// per-call packet loss / jitter / RTT / MOS is
						// diagnosable after the fact. Runs regardless of
						// whether the provider computes warnings itself
						// (Twilio raises native warnings and skips the
						// computer, but the raw numbers still matter).
						samplesSincePersist++;
						if (samplesSincePersist >= QUALITY_SAMPLE_PERSIST_EVERY) {
							samplesSincePersist = 0;
							batcher?.record({
								eventType: 'quality.sample',
								callId: call.id,
								role: roleRef.current,
								payload: { ...stats.audio },
							});
						}
						consecutiveSamplingFailures = 0;
					} catch (err) {
						// Per-failure logs would be too noisy at GA volume,
						// but a consecutive-failure burst is a real signal
						// (the SDK may have lost its peer connection without
						// firing `disconnected`). Surface a `device.error`
						// observability event after 3 in a row so on-call
						// dashboards can alert without us also spamming the
						// console on every transient blip.
						consecutiveSamplingFailures++;
						if (consecutiveSamplingFailures === 3) {
							batcher?.record({
								eventType: 'device.error',
								callId: call.id,
								role: roleRef.current,
								payload: {
									code: 'stats-sampling-failure',
									message:
										err instanceof Error
											? err.message
											: 'getStats() threw repeatedly',
									consecutive: consecutiveSamplingFailures,
								},
							});
						}
					}
				})();
			}, samplingMs);
		}

		const recordEvent = (
			eventType: SoftphoneObservabilityEventTypeModel,
			payload: Record<string, unknown> = {},
			callId: string | null = null,
		): void => {
			batcher?.record({
				eventType,
				callId,
				role: roleRef.current,
				payload,
			});
		};

		const onIncomingCall = (call: SoftphoneCallModel): void => {
			const view: SoftphoneCallView = {
				id: call.id,
				direction: call.direction,
				fromE164: call.fromE164,
				toE164: call.toE164,
				state: call.state,
				isMuted: call.isMuted(),
				isOnHold: call.isOnHold(),
				callerIdMatch: call.callerIdMatch,
				persistedCallId: call.persistedCallId,
				recordingActive: call.recordingActive,
				transferContext: call.transferContext ?? null,
				startedAt: Date.now(),
				connectedAt: null,
				activeQualityWarnings: [],
			};
			// Call-waiting: if a call is already active, the new
			// inbound goes into the `pendingCall` slot and the
			// `<PendingCallBanner>` surfaces it. The previous build
			// silently overwrote currentCall, dropping the original
			// call's audio reference and leaving the rep with no
			// indication a second call had arrived.
			const existingCall = useSoftphoneStore.getState().currentCall;
			if (existingCall != null) {
				useSoftphoneStore.getState()._setPendingCall(view);
				// `pendingCallRef` lets `acceptPendingCall` find the
				// underlying SDK call object once the rep ends the
				// current call and accepts the pending one.
				pendingCallRef.current = call;
				wireCallEvents(call);
				recordEvent(
					'call.incoming',
					{
						fromE164: call.fromE164,
						toE164: call.toE164,
						pending: true,
					},
					call.id,
				);
				return;
			}

			useSoftphoneStore.getState()._setCurrentCall(view);
			callRef.current = call;
			qualityComputer?.reset();
			wireCallEvents(call);
			recordEvent(
				'call.incoming',
				{ fromE164: call.fromE164, toE164: call.toE164 },
				call.id,
			);
			bus.post({ type: 'leader-state', hasActiveCall: true });

			// Tier 2 coaching: if the user is currently the coach in an
			// active session, this incoming call is the coaching leg the
			// backend just dialed them. Auto-accept so audio flows
			// without a manual click. The `<CoachingToolbar>` already
			// shows the session UI; the call-pop is suppressed
			// elsewhere.
			//
			// Race note: the WS hint that populates `activeCoachingSession`
			// might arrive AFTER this `incomingCall` event. The live-monitor
			// start path eagerly populates the store before the hint
			// lands to close the gap; if the eager-set is missing
			// (e.g. coaching started from a different surface), the
			// pop-suppression won't happen but the manual answer flow
			// still works as a fallback.
			const activeSession = useSoftphoneStore.getState().activeCoachingSession;
			if (activeSession != null) {
				void call.answer().catch((err) => {
					recordEvent(
						'call.error',
						{
							code: 'coaching-auto-answer-failed',
							message: err instanceof Error ? err.message : String(err),
						},
						call.id,
					);
				});
			}
		};

		const onRegistered = (): void => {
			useSoftphoneStore.getState()._setRegistered(true);
			useSoftphoneStore.getState()._setConnectionState('online');
			recordEvent('device.register.success');
		};
		const onUnregistered = (): void => {
			useSoftphoneStore.getState()._setRegistered(false);
			useSoftphoneStore.getState()._setConnectionState('offline');
			recordEvent('device.unregister');
		};
		const onDeviceReconnecting = (info: {
			attempt: number;
			reason: string;
		}): void => {
			useSoftphoneStore.getState()._setConnectionState('reconnecting');
			recordEvent('device.reconnecting', info);
		};
		const onDeviceReconnected = (): void => {
			useSoftphoneStore.getState()._setConnectionState('online');
			recordEvent('device.reconnected');
		};
		const onDeviceError = (error: {
			code: string;
			message: string;
			details?: Record<string, unknown>;
		}): void => {
			recordEvent('device.error', {
				code: error.code,
				message: error.message,
				// Diagnostic detail, e.g. which microphone tracks were left open.
				...(error.details != null ? { details: error.details } : {}),
			});
		};

		device.on('incomingCall', onIncomingCall);
		device.on('registered', onRegistered);
		device.on('unregistered', onUnregistered);
		device.on('reconnecting', onDeviceReconnecting);
		device.on('reconnected', onDeviceReconnected);
		device.on('deviceError', onDeviceError);

		const unsubscribeRole = election.onRoleChange((role) => {
			roleRef.current = role;
			useSoftphoneStore.getState()._setRole(role);
			recordEvent(role === 'leader' ? 'leader.acquired' : 'leader.released');
			if (role === 'leader') {
				void device.register();
			} else {
				void device.unregister();
			}
		});

		const unsubscribeBus = bus.on((msg) => {
			if (msg.type === 'leader-state') {
				// Leader broadcast its idle/active status — followers
				// mirror into store so banner copy stays accurate.
				if (typeof msg.hasActiveCall === 'boolean') {
					useSoftphoneStore
						.getState()
						._setLeaderHasActiveCall(msg.hasActiveCall);
				}
				return;
			}
			if (msg.type === 'leadership-denied') {
				useSoftphoneStore.getState()._setLeadershipDeniedAt(Date.now());
				return;
			}
			if (msg.type === 'request-leadership' && election.isLeader()) {
				// Active-call guard: if a follower asks the leader to
				// step down WHILE THE LEADER IS ON A CALL, releasing
				// the lock would tear the device down (the role-change
				// handler calls `device.unregister()`) and drop the
				// audio mid-conversation. Reply with a denial; the
				// follower's banner surfaces the reason. True call
				// handoff between tabs requires server-side conference
				// rebridge (Phase 2.4+ when hold/transfer REST land).
				if (callRef.current != null) {
					bus.post({
						type: 'leadership-denied',
						reason: 'call-in-progress',
					});
					recordEvent('leader.handoff-denied', {
						reason: 'call-in-progress',
					});
					return;
				}
				election.release();
				// Broadcast new leader-state (idle, no call) so other
				// tabs can update their banner copy.
				bus.post({ type: 'leader-state', hasActiveCall: false });
				return;
			}
			if (
				msg.type === 'dial-intent' &&
				election.isLeader() &&
				typeof msg.toE164 === 'string'
			) {
				void (async () => {
					const target = msg.toE164 ?? '';
					const fromCallerId =
						typeof msg.fromCallerId === 'string' ? msg.fromCallerId : undefined;
					const crmContext = msg.crmContext;
					try {
						const call = await device.dial(target, {
							fromCallerId,
							fromCrmContext:
								crmContext != null
									? {
											entityType: crmContext.entityType,
											entityId: crmContext.entityId,
										}
									: undefined,
						});
						callRef.current = call;
						useSoftphoneStore.getState()._setCurrentCall({
							id: call.id,
							direction: 'outbound',
							fromE164: call.fromE164,
							toE164: call.toE164,
							state: call.state,
							isMuted: call.isMuted(),
							isOnHold: call.isOnHold(),
							// Relayed click-to-dial context (PD-14414) — same
							// instant-label semantics as the leader dial path.
							callerIdMatch:
								crmContext != null
									? buildClickContextMatch(crmContext, target)
									: null,
							callerIdMatchAuthoritative: crmContext != null,
							persistedCallId: null,
							recordingActive: false,
							transferContext: null,
							startedAt: Date.now(),
							connectedAt: null,
							activeQualityWarnings: [],
						});
						wireCallEvents(call);
						recordEvent(
							'call.outgoing',
							{ toE164: target, viaBus: true },
							call.id,
						);
						bus.post({ type: 'leader-state', hasActiveCall: true });
					} catch (err) {
						// Surface the failure back to the originating
						// tab so its toast / status indicator can update
						// — without this, fire-and-forget dial intents
						// silently no-op from the follower's POV.
						bus.post({
							type: 'dial-failed',
							toE164: target,
							error:
								err instanceof Error
									? err.message
									: 'Dial failed for unknown reason',
						});
						recordEvent('call.outgoing.failed', {
							toE164: target,
							error: err instanceof Error ? err.message : 'unknown',
						});
					}
				})();
			}
		});

		void election.waitForLeadership();

		// Initial device-list seed + refresh on `devicechange`. Reps
		// commonly use Bluetooth headsets that disconnect mid-call —
		// without this listener the dropdown shows stale entries and
		// the SDK silently falls back to the system default. With it,
		// disappearing devices clear the selected slot and the next
		// outbound call uses the default mic/speaker.
		const refreshDevices = (): void => {
			void Promise.all([
				device.getInputDevices(),
				device.getOutputDevices(),
			]).then(([inputs, outputs]) => {
				useSoftphoneStore.getState()._setDevices(inputs, outputs);

				const state = useSoftphoneStore.getState();
				const selectedInput = state.selectedInputDeviceId;
				if (
					selectedInput != null &&
					!inputs.some((d) => d.deviceId === selectedInput)
				) {
					state._setSelectedInput(null);
					recordEvent('device.error', {
						code: 'input-device-removed',
						message: 'Selected microphone is no longer available',
						previousDeviceId: selectedInput,
					});
				}
				const selectedOutput = state.selectedOutputDeviceId;
				if (
					selectedOutput != null &&
					!outputs.some((d) => d.deviceId === selectedOutput)
				) {
					state._setSelectedOutput(null);
					recordEvent('device.error', {
						code: 'output-device-removed',
						message: 'Selected speaker is no longer available',
						previousDeviceId: selectedOutput,
					});
				}
			});
		};
		refreshDevices();

		const mediaDevices =
			typeof navigator !== 'undefined' ? navigator.mediaDevices : null;
		// Optional chain — `mediaDevices` is undefined in some test envs
		// and on very-old browsers. The listener call below is no-op
		// when not present.
		mediaDevices?.addEventListener?.('devicechange', refreshDevices);

		return () => {
			device.off('incomingCall', onIncomingCall);
			device.off('registered', onRegistered);
			device.off('unregistered', onUnregistered);
			device.off('reconnecting', onDeviceReconnecting);
			device.off('reconnected', onDeviceReconnected);
			device.off('deviceError', onDeviceError);
			mediaDevices?.removeEventListener?.('devicechange', refreshDevices);
			unsubscribeRole();
			unsubscribeBus();
			if (statSamplingHandle != null) clearInterval(statSamplingHandle);
			// PD-14895 — a teardown here always disposes the device, which
			// hangs up any call in progress (Twilio reports it as
			// `disconnected-by-local`, indistinguishable from the rep
			// hanging up). That is correct ONLY when the identity really
			// changed. Deferring the dispose instead would be worse: the
			// effect re-runs immediately and builds a NEW device, election
			// and bus, orphaning the live call behind refs nothing points
			// at any more. So the invariant is enforced upstream — this
			// effect must depend only on values that change when the
			// session genuinely changes. See the dependency array below.
			if (callRef.current != null) {
				recordEvent('provider.teardown-during-call', {
					reason: 'resource-key-changed',
				});
			}
			recordEventRef.current = null;
			void batcher?.dispose();
			void device.dispose();
			election.dispose();
			bus.dispose();
			useSoftphoneStore.getState()._reset();
			deviceRef.current = null;
			callRef.current = null;
			electionRef.current = null;
			busRef.current = null;
		};
		// `observability` and `deviceFactory` deliberately omitted —
		// both are read out of their refs at mount time. See those ref
		// declarations for the design rationale. `resourceKey` is a
		// plain string, so it is compared by VALUE: the effect re-runs
		// only when the tenant/user identity genuinely changes, which
		// is the one case where tearing the device down is correct.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [resourceKey, wireCallEvents]);

	const performCallAction = useCallback(
		async (
			fn: (call: SoftphoneCallModel) => Promise<void> | void,
		): Promise<void> => {
			const call = callRef.current;
			if (call == null) return;
			await fn(call);
			useSoftphoneStore.getState()._patchCurrentCall({
				isMuted: call.isMuted(),
				isOnHold: call.isOnHold(),
			});
		},
		[],
	);

	const dial = useCallback(
		async (
			toE164: string,
			options?: SoftphoneDialOptionsModel,
		): Promise<void> => {
			const election = electionRef.current;
			const device = deviceRef.current;
			const bus = busRef.current;
			if (election == null || device == null || bus == null) return;
			if (!election.isLeader()) {
				bus.post({
					type: 'dial-intent',
					toE164,
					fromCallerId: options?.fromCallerId,
					crmContext: options?.crmContext,
				});
				return;
			}

			// RAY BAUM §506 — pre-flight E911 gate. Skip for 911 / 933 /
			// 988 (the bypass list also enforced server-side; checking
			// here avoids a needless status round-trip on a true emergency
			// dial). For any other target, fetch the rep's attestation
			// status and surface the re-attestation modal instead of
			// letting the dial fail at Twilio. The modal's [Confirm]
			// handler retries this same `dial` call after attestation
			// succeeds.
			if (!isE911BypassTarget(toE164)) {
				try {
					const status = await SoftphoneSelfServiceQueryService.getE911Status();
					if (status.isAttestationExpired) {
						useSoftphoneStore.getState()._setE911GatedDial({
							toE164,
							fromCallerId: options?.fromCallerId ?? null,
							crmContext: options?.crmContext ?? null,
							address: status.address,
							hasAddress: status.hasAddress,
						});
						return;
					}
				} catch {
					// Best-effort: if the status fetch fails (network blip,
					// backend down), fall through to the dial attempt. The
					// backend gate in resolveOutbound is the safety net —
					// a failed call gets rejected there with the same
					// reason code, just with worse UX (no modal).
				}
			}

			const crmContext = options?.crmContext;
			const call = await device.dial(toE164, {
				fromCallerId: options?.fromCallerId,
				fromCrmContext:
					crmContext != null
						? {
								entityType: crmContext.entityType,
								entityId: crmContext.entityId,
							}
						: undefined,
			});
			callRef.current = call;
			useSoftphoneStore.getState()._setCurrentCall({
				id: call.id,
				direction: 'outbound',
				fromE164: call.fromE164,
				toE164: call.toE164,
				state: call.state,
				isMuted: call.isMuted(),
				isOnHold: call.isOnHold(),
				// Click-to-dial: the rep told us who they're calling —
				// show it immediately instead of waiting for (or being
				// overwritten by) the phone-map guess (PD-14414).
				callerIdMatch:
					crmContext != null
						? buildClickContextMatch(crmContext, toE164)
						: null,
				callerIdMatchAuthoritative: crmContext != null,
				persistedCallId: null,
				recordingActive: false,
				transferContext: null,
				startedAt: Date.now(),
				connectedAt: null,
				activeQualityWarnings: [],
			});
			wireCallEvents(call);
			recordEventRef.current?.({
				eventType: 'call.outgoing',
				callId: call.id,
				role: roleRef.current,
				payload: { toE164 },
			});
			bus.post({ type: 'leader-state', hasActiveCall: true });
		},
		[wireCallEvents],
	);

	const setInputDevice = useCallback(
		async (deviceId: string): Promise<void> => {
			const device = deviceRef.current;
			if (device == null) return;
			await device.setInputDevice(deviceId);
			useSoftphoneStore.getState()._setSelectedInput(deviceId);
		},
		[],
	);

	const setOutputDevice = useCallback(
		async (deviceId: string): Promise<void> => {
			const device = deviceRef.current;
			if (device == null) return;
			await device.setOutputDevice(deviceId);
			useSoftphoneStore.getState()._setSelectedOutput(deviceId);
		},
		[],
	);

	// ----- Audio tuning (PD-14902) -----
	//
	// Each setter mutates the device (which applies live to the current
	// call), then reads the device's authoritative settings back into
	// the store and persistence. Reading back — rather than trusting our
	// input — means the store reflects clamping the device applied.

	const syncAudioSettings = useCallback((): void => {
		const device = deviceRef.current;
		if (device == null) {
			return;
		}
		const settings = device.getAudioSettings();
		useSoftphoneStore.getState()._setAudioSettings(settings);
		persistAudioSettings(settings);
	}, []);

	const setInputGain = useCallback(
		(gain: number): void => {
			deviceRef.current?.setInputGain(gain);
			syncAudioSettings();
		},
		[syncAudioSettings],
	);

	const setOutputVolume = useCallback(
		(volume: number): void => {
			deviceRef.current?.setOutputVolume(volume);
			syncAudioSettings();
		},
		[syncAudioSettings],
	);

	const setReceivedAudioEnhancement = useCallback(
		(enabled: boolean): void => {
			deviceRef.current?.setReceivedAudioEnhancement(enabled);
			syncAudioSettings();
		},
		[syncAudioSettings],
	);

	const setAudioCaptureSettings = useCallback(
		async (capture: Partial<SoftphoneAudioCaptureModel>): Promise<void> => {
			const device = deviceRef.current;
			if (device == null) {
				return;
			}
			await device.setAudioCaptureSettings(capture);
			syncAudioSettings();
		},
		[syncAudioSettings],
	);

	const requestLeadership = useCallback((): void => {
		const election = electionRef.current;
		const bus = busRef.current;
		if (election == null || bus == null) return;
		// Tell the current leader (if any) to release. Even without a
		// reply, our own waitForLeadership keeps queued so we'll take
		// over as soon as the lock frees.
		bus.post({ type: 'request-leadership' });
		void election.waitForLeadership();
	}, []);

	const acceptPendingCall = useCallback(async (): Promise<void> => {
		const pending = pendingCallRef.current;
		if (pending == null) return;
		const current = callRef.current;
		// Hang up the current call first; the SDK fires `disconnect`
		// which clears `currentCall` and surfaces the wrap-up panel.
		// Then promote the pending call to the active slot and accept.
		if (current != null) {
			try {
				await current.hangup();
			} catch {
				// Best-effort — even if the hangup throws, we proceed
				// to accept the pending call so the rep isn't stuck.
			}
		}
		const pendingView = useSoftphoneStore.getState().pendingCall;
		useSoftphoneStore.getState()._setPendingCall(null);
		pendingCallRef.current = null;
		if (pendingView != null) {
			useSoftphoneStore.getState()._setCurrentCall(pendingView);
			callRef.current = pending;
		}
		await pending.answer();
	}, []);

	const declinePendingCall = useCallback(async (): Promise<void> => {
		const pending = pendingCallRef.current;
		if (pending == null) return;
		try {
			await pending.decline();
		} finally {
			useSoftphoneStore.getState()._setPendingCall(null);
			pendingCallRef.current = null;
		}
	}, []);

	const getLocalAudioStream = useCallback((): MediaStream | null => {
		const call = callRef.current;
		if (call == null) return null;
		// Provider-agnostic: not all SoftphoneCallModel implementations
		// expose the underlying stream. Returning null is the documented
		// "no meter" path.
		return call.getLocalAudioStream?.() ?? null;
	}, []);

	const actions: SoftphoneActionsModel = useMemo(
		() => ({
			dial,
			answer: () => performCallAction((c) => c.answer()),
			decline: () => performCallAction((c) => c.decline()),
			hangup: () => performCallAction((c) => c.hangup()),
			mute: () => performCallAction((c) => c.mute()),
			unmute: () => performCallAction((c) => c.unmute()),
			hold: () => performCallAction((c) => c.hold()),
			unhold: () => performCallAction((c) => c.unhold()),
			sendDTMF: (digits: string) =>
				performCallAction((c) => c.sendDTMF(digits)),
			blindTransfer: (target: string) =>
				performCallAction((c) => c.blindTransfer(target)),
			setInputDevice,
			setOutputDevice,
			setInputGain,
			setOutputVolume,
			setReceivedAudioEnhancement,
			setAudioCaptureSettings,
			requestLeadership,
			acceptPendingCall,
			declinePendingCall,
			getLocalAudioStream,
		}),
		[
			dial,
			performCallAction,
			setInputDevice,
			setOutputDevice,
			setInputGain,
			setOutputVolume,
			setReceivedAudioEnhancement,
			setAudioCaptureSettings,
			requestLeadership,
			acceptPendingCall,
			declinePendingCall,
			getLocalAudioStream,
		],
	);

	return (
		<SoftphoneActionsContext.Provider value={actions}>
			{children}
		</SoftphoneActionsContext.Provider>
	);
};

/**
 * Internal hook for the actions context — mainly an implementation
 * detail of useSoftphone / useCallControls. Exported for cases
 * where a component genuinely only wants the actions and not any
 * subscribed state.
 */
export function useSoftphoneActions(): SoftphoneActionsModel {
	const ctx = useContext(SoftphoneActionsContext);
	if (ctx == null) {
		throw new Error(
			'useSoftphoneActions must be used inside a <SoftphoneProvider>',
		);
	}
	return ctx;
}

/**
 * Returns null when no `<SoftphoneProvider>` is mounted, instead of
 * throwing. Use in components that must work both inside and outside
 * a softphone-enabled context (e.g., the global click-to-dial widget,
 * which renders on every page even if the rep hasn't enabled the
 * softphone or the tenant hasn't provisioned it).
 */
export function useSoftphoneActionsSafe(): SoftphoneActionsModel | null {
	return useContext(SoftphoneActionsContext);
}
